<template>
    <nav
        class="flex flex-col rounded-2xl border border-valence-cream-200 bg-white px-4 py-2 overflow-y-auto"
        aria-label="Performance cycle progress"
        data-testid="performance-cycle-tracker">
        <div v-if="isFetching || !moment" class="flex items-center gap-3 px-2.5 py-4 text-valence-stone-500 scaffold-body-small">
            <LoadingSpinner class="w-4.5 h-4.5" />
            Loading your cycle…
        </div>
        <template v-else>
            <!-- No moment title here: the chat's own title row names the cycle. -->
            <!-- gap-1 keeps consecutive rows' hover/highlight backgrounds from touching. -->
            <div class="flex flex-col gap-1 pt-1 pb-2.5" :data-testid="`tracker-detail-${moment.id}`">
                <!-- Self tasks -->
                <h3 v-if="selfReviews.length && selfTitle" class="scaffold-details-small text-valence-stone-500 px-2.5 pt-2 pb-1" data-testid="tracker-self-header">
                    {{ selfTitle }}
                </h3>
                <PickerRow
                    v-for="review in selfReviews"
                    :key="review.id"
                    :review="review"
                    :current-conversation-hashid="currentConversationHashid"
                    :is-busy="isLaunching"
                    :start-disabled="isExpired"
                    :highlighted="isCurrentSession(review)"
                    @row-clicked="handleRowClick" />
                <!-- Reviewees -->
                <h3
                    v-if="relationshipGroups.length && otherTitle"
                    class="scaffold-details-small text-valence-stone-500 px-2.5 pt-3 pb-1 mt-2 border-t border-valence-cream-200"
                    data-testid="tracker-other-header">
                    {{ otherTitle }}
                </h3>
                <div v-for="group in relationshipGroups" :key="group.relationshipId" class="flex flex-col gap-1" :data-testid="`tracker-person-${group.relationshipId}`">
                    <button
                        type="button"
                        class="group flex w-full items-center gap-2.5 rounded-md px-2.5 py-2.5 text-start transition-colors hover:bg-valence-cream-50"
                        :class="{ 'bg-valence-cream-50': isCurrentGroup(group) }"
                        :aria-expanded="group.reviews.length > 1 ? isGroupExpanded(group) : undefined"
                        @click="handleGroupClick(group)">
                        <BaseAvatar
                            size="xs"
                            theme="identity"
                            :identity-key="group.reviews[0].relationship?.reviewee_name ?? '?'"
                            :initials="firstInitial(group.reviews[0].relationship?.reviewee_name)" />
                        <span class="grow min-w-0 body-small text-valence-stone-900 truncate">{{ group.reviews[0].relationship?.reviewee_name }}</span>
                        <span class="flex items-center gap-3 shrink-0 scaffold-details-small text-valence-stone-500">
                            <template v-if="group.reviews.length === 1 && isCurrentSession(group.reviews[0])">Started</template>
                            <template v-else>{{ getReviewGroupStatus(group.reviews) }}</template>
                            <!-- Fixed-width slot (chevron / hover arrow / empty) keeps statuses right-aligned across rows. -->
                            <i class="bi w-3.5 shrink-0 text-sm/none" :class="trailingIconClass(group)" aria-hidden="true"></i>
                        </span>
                    </button>
                    <div v-if="group.reviews.length > 1 && isGroupExpanded(group)" class="flex flex-col gap-1 pl-8">
                        <PickerRow
                            v-for="review in group.reviews"
                            :key="review.id"
                            :review="review"
                            :current-conversation-hashid="currentConversationHashid"
                            :is-busy="isLaunching"
                            :start-disabled="isExpired"
                            :highlighted="isCurrentSession(review)"
                            @row-clicked="handleRowClick" />
                    </div>
                </div>
            </div>
        </template>
        <BaseToast v-model:open="isErrorToastOpen" theme="error" :description="launchError" />
    </nav>
</template>

<script setup lang="ts">
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { usePerformancePicker } from "~vue/Moments/composables/usePerformancePicker";
import { getReviewGroupStatus } from "~vue/Moments/composables/useReviewGroupStatus";
import { useStartPerformanceReviewConversation } from "~vue/Moments/composables/useStartPerformanceReviewConversation";
import type { DecoratedPerformanceReview, PerformanceReviewContext } from "~vue/types/performance_management";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { sortBy, uniq } from "lodash";
import { computed, inject, onMounted, ref } from "vue";

import PickerRow from "./ChatPerformancePickerRow.vue";

interface Props {
    context: PerformanceReviewContext;
}

const props = defineProps<Props>();

// Hashid of the coaching session the tracker is rendered beside — its own rows are inert.
const currentConversationHashid = inject<string>("coachingSessionId", "");

// The tracker is scoped to the chat's own moment; other cycle moments live on the PM page.
const momentId = ref<number | null>(props.context.moment_id);

const { data: pickerData, isFetching, execute, decoratedReviews } = usePerformancePicker(momentId);

const moment = computed(() => pickerData.value?.moment ?? null);
const isExpired = computed(() => moment.value?.state === "expired");

const selfReviews = computed(() => decoratedReviews.value.filter((review) => review.cta?.reviewer_target === "self"));
const teamReviews = computed(() => decoratedReviews.value.filter((review) => review.cta?.reviewer_target === "connection"));

const firstInitial = (name: string | null | undefined): string => (name ?? "").trim().charAt(0).toUpperCase();

onMounted(async () => {
    await execute();
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_TRACKER_VIEWED, { moment_id: props.context.moment_id });
});

// --- Reviewee groups (mirrors PmPickerPersonPhase's grouping) ---

type RelationshipGroup = { relationshipId: string; reviews: DecoratedPerformanceReview[] };

const relationshipGroups = computed<RelationshipGroup[]>(() => {
    const withRelationships = teamReviews.value.filter((review) => !!review.relationship);
    const relationshipIds = uniq(sortBy(withRelationships, (review) => review.relationship?.reviewee_name).map((review) => String(review.relationship?.id)));
    return relationshipIds.map((relationshipId) => ({
        relationshipId,
        reviews: sortBy(
            withRelationships.filter((review) => String(review.relationship?.id) === relationshipId),
            (review) => review.cta?.id,
        ),
    }));
});

// Section headers, mirroring the PM page's naming: org-configured moment labels win, generic
// fallbacks otherwise — and no header at all when only one section renders.
const selfTitle = computed(() => moment.value?.self_moment?.title || (relationshipGroups.value.length ? "Myself" : ""));
const otherTitle = computed(() => moment.value?.other_moment?.title || (selfReviews.value.length ? "My Team" : ""));

const isCurrentSession = (review: DecoratedPerformanceReview) =>
    !!review.conversation?.coaching_session_hashid && review.conversation.coaching_session_hashid === currentConversationHashid;

// The person this chat is about — highlighted as the "you are here" row.
const isCurrentGroup = (group: RelationshipGroup) => group.relationshipId === props.context.relationship_id;

const expandedGroups = ref<Record<string, boolean>>({});
const isGroupExpanded = (group: RelationshipGroup) => expandedGroups.value[group.relationshipId] ?? isCurrentGroup(group);

// Multi-task groups toggle (chevron); single-task rows navigate (hover arrow), except the
// row for this very chat, which is inert (empty slot).
const trailingIconClass = (group: RelationshipGroup): string => {
    if (group.reviews.length > 1) {
        return isGroupExpanded(group) ? "bi-chevron-up" : "bi-chevron-down";
    }
    if (isCurrentSession(group.reviews[0])) {
        return "";
    }
    return "bi-arrow-right text-valence-stone-900 opacity-0 transition-opacity group-hover:opacity-100";
};

const handleGroupClick = (group: RelationshipGroup) => {
    if (group.reviews.length === 1) {
        if (!isCurrentSession(group.reviews[0])) {
            handleRowClick(group.reviews[0]);
        }
        return;
    }
    expandedGroups.value[group.relationshipId] = !isGroupExpanded(group);
};

// --- Navigation (same semantics as ChatPerformancePicker.handleRowClick) ---

const { isLaunching, launchError, startConversation } = useStartPerformanceReviewConversation();

const isErrorToastOpen = ref(false);

const handleRowClick = async (review: DecoratedPerformanceReview) => {
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_TRACKER_ROW_CLICKED, {
        moment_id: moment.value?.id,
        review_id: review.id,
        cta_id: review.performance_moment_cta_id,
        has_conversation: !!review.conversation,
        // Mirrors the picker's row-click payload so the two surfaces are comparable.
        reviewer_target: review.cta?.reviewer_target ?? null,
        relationship_id: review.relationship?.id ? String(review.relationship.id) : null,
    });

    // Resume/View: the conversation already exists — plain navigation, never reseed.
    if (review.conversation?.coaching_session_url) {
        openUrl(review.conversation.coaching_session_url);
        return;
    }

    // Expired cycles allow viewing existing conversations but never new starts.
    if (isExpired.value) {
        return;
    }

    const url = await startConversation(review);
    if (!url) {
        // A session-ended launch resolves null with an empty launchError: the modal dialog owns
        // that messaging, and a blank toast behind it would only add noise.
        if (launchError.value) {
            isErrorToastOpen.value = true;
        }
        return;
    }
    openUrl(url);
};
</script>
